import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { floorplanAreaLegendGroups } from '../../../components/floorplan/areaAppearance';
import { TeamDeskSwatch } from '../../../components/floorplan/teamColors';
import { focusRing, sbg, transition } from '../../../theme/sbgTokens';
import { formatCount, formatNumber } from '../../../utils/format';

const Swatch: React.FC<{ background: string; border: string; label: React.ReactNode }> = ({ background, border, label }) => (
  <Box component="li" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
    <Box
      aria-hidden="true"
      sx={{ width: 16, height: 12, flex: 'none', boxSizing: 'border-box', borderRadius: '3px', bgcolor: background, border }}
    />
    <Typography variant="body2" component="span" noWrap sx={{ color: sbg.text.secondary }}>
      {label}
    </Typography>
  </Box>
);

const Group: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', minWidth: 0 }}>
    <Typography variant="labelXs" component="span" sx={{ color: sbg.text.primary }}>
      {label}
    </Typography>
    <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', alignItems: 'center', columnGap: 1.5, rowGap: 0.5, flexWrap: 'wrap' }}>
      {children}
    </Box>
  </Box>
);

export interface LegendTeam {
  name: string;
  /** The team's identity color (hex) from the API. */
  color: string;
  /** Desks the team holds on the floor in view. */
  deskCount: number;
}

/** A team in the legend: its desk swatch, name and desk count. Pressing it picks the team out on the floor. */
const TeamChip: React.FC<{ team: LegendTeam; pressed: boolean; onToggle?: (teamName: string) => void }> = ({ team, pressed, onToggle }) => {
  const label = `${team.name}, ${team.deskCount ? formatCount(team.deskCount, 'desk') : 'none on this floor'}`;
  const content = (
    <>
      <TeamDeskSwatch color={team.color} strong={pressed} />
      <Typography variant="labelSm" component="span" noWrap sx={{ color: sbg.text.primary }}>
        {team.name}
      </Typography>
      <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
        {formatNumber(team.deskCount)}
      </Typography>
    </>
  );
  return (
    <Box component="li" sx={{ display: 'inline-flex', minWidth: 0 }}>
      {onToggle ? (
        <ButtonBase
          aria-pressed={pressed}
          aria-label={label}
          title={pressed ? 'Show every team' : `Pick out ${team.name}`}
          onClick={() => onToggle(team.name)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            height: 26,
            px: 1,
            borderRadius: sbg.radius.pill,
            border: `1px solid ${pressed ? sbg.border.selected : 'transparent'}`,
            bgcolor: pressed ? sbg.bg.selected : 'transparent',
            transition: transition('background-color', 'border-color'),
            '&:hover': { bgcolor: pressed ? sbg.bg.selectedStrong : sbg.bg.hover },
            '&.Mui-focusVisible': focusRing,
          }}
        >
          {content}
        </ButtonBase>
      ) : (
        <Box component="span" aria-label={label} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, height: 26, px: 1 }}>
          {content}
        </Box>
      )}
    </Box>
  );
};

interface CanvasLegendProps {
  /** Area types present on the floor. */
  areaTypes: string[];
  /** Teams seated on the floor in view, most desks first, with their colors. */
  teams?: LegendTeam[];
  /** The team picked out on the floor, with its desk count on this floor. */
  highlightedTeam?: { name: string; deskCount: number; color?: string } | null;
  /** Pick a team out on the floor (pressing the picked team clears it). */
  onTeamToggle?: (teamName: string) => void;
}

/**
 * What the drawing's symbols mean: the teams seated on this floor in their
 * colors (pressable, to pick one out), desk states, and the neutral space
 * symbols present on this floor.
 */
const CanvasLegend: React.FC<CanvasLegendProps> = ({ areaTypes, teams = [], highlightedTeam, onTeamToggle }) => {
  const spaces = floorplanAreaLegendGroups(areaTypes);
  // A team picked from the search box may sit on another floor; keep it visible and pressable here.
  const shownTeams =
    highlightedTeam && !teams.some((team) => team.name === highlightedTeam.name)
      ? [...teams, { name: highlightedTeam.name, color: highlightedTeam.color ?? '', deskCount: highlightedTeam.deskCount }]
      : teams;
  return (
    <Box aria-label="Floor plan legend" role="group" sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
      {shownTeams.length > 0 && (
        <Group label="Teams">
          {shownTeams.map((team) => (
            <TeamChip key={team.name} team={team} pressed={highlightedTeam?.name === team.name} onToggle={onTeamToggle} />
          ))}
        </Group>
      )}
      <Group label="Desks">
        <Swatch background={sbg.bg.surface} border={`1px dashed ${sbg.border.strong}`} label="Open" />
        <Swatch background={sbg.bg.selected} border={`2px solid ${sbg.border.selected}`} label="Selected" />
      </Group>
      {spaces.length > 0 && (
        <Group label="Spaces">
          {spaces.map(({ key, label, appearance }) => (
            <Swatch
              key={key}
              background={appearance.background}
              border={`${Math.min(appearance.strokeWidth, 2)}px ${appearance.dashed ? 'dashed' : 'solid'} ${appearance.stroke}`}
              label={label}
            />
          ))}
        </Group>
      )}
    </Box>
  );
};

export default CanvasLegend;
